import { createContext, useContext, type ReactNode } from 'react';

export type ConnectionStatus = 'active' | 'reauthentication_required' | 'disabled';

export type OperatorSession = {
  email: string;
};

export type WorkspaceConnection = {
  id: string;
  name: string;
  status: ConnectionStatus;
};

export type WorkspaceLibrary = {
  id: string;
  name: string;
  status: 'active' | 'disabled';
};

export type Workspace = {
  connection: WorkspaceConnection;
  library: WorkspaceLibrary;
};

export type SessionResource =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | {
      status: 'unavailable';
      error: unknown;
      retry: () => void;
      retrying: boolean;
    }
  | { status: 'authenticated'; session: OperatorSession };

export type WorkspaceResource =
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'error'; error: Error; retry: (() => void) | undefined }
  | { status: 'ready'; workspace: Workspace };

const WorkspaceContext = createContext<Workspace | null>(null);

export function WorkspaceProvider({
  workspace,
  children,
}: {
  workspace: Workspace;
  children: ReactNode;
}) {
  return <WorkspaceContext.Provider value={workspace}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): Workspace {
  const workspace = useContext(WorkspaceContext);
  if (!workspace) throw new Error('useWorkspace must be used inside WorkspaceProvider.');
  return workspace;
}
